/**
 * Formas de pagamento no checkout da loja pública.
 *
 * Os valores (`value`) viajam até à API e batem com a normalização aplicada por
 * `ominia_normalize_checkout_payment_label` (CAIXA ALTA, sem acentos nem
 * caracteres especiais). Os rótulos (`display`) é o que o cliente vê.
 */

/** Rótulo (display) mostrado no combo para a opção que dispara o Mercado Pago. */
export const CHECKOUT_PM_ONLINE_DISPLAY = 'PAGAR ONLINE (MERCADO PAGO)';

/** Valor (value) usado internamente / enviado para a API quando o cliente escolhe pagar online. */
export const CHECKOUT_PM_ONLINE_VALUE = 'PAGAR ONLINE MERCADO PAGO';

/** Rótulo da opção offline padrão (injectada só quando o lojista não definiu nenhuma forma). */
export const CHECKOUT_PM_OFFLINE_FALLBACK_DISPLAY = 'PAGAR NA ENTREGA / RETIRADA';

/** Valor (normalizado) da opção offline padrão. */
export const CHECKOUT_PM_OFFLINE_FALLBACK_VALUE = 'PAGAR NA ENTREGA RETIRADA';

/**
 * Normaliza um rótulo de forma de pagamento para corresponder à regra do
 * backend (`ominia_normalize_checkout_payment_label`): caixa alta, sem
 * acentos nem caracteres especiais, espaços colapsados.
 */
export function normalizeCheckoutPaymentLabel(raw) {
  const str = String(raw ?? '').trim();
  if (!str) return '';
  const noAccents = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return noAccents
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @typedef {object} CheckoutPaymentOption
 * @property {string}  value     String enviada à API (normalizada).
 * @property {string}  display   Texto mostrado ao cliente.
 * @property {'online'|'offline-fallback'|'tenant'} kind  Origem da opção.
 * @property {string=} subtitle  Subtítulo opcional (ex.: "Cartão · Pix · Boleto").
 */

/**
 * Devolve a lista efectiva de formas de pagamento para o checkout, já com as
 * opções automáticas (Mercado Pago + fallback offline) quando aplicável.
 *
 * Regras:
 * - Sem pagamento online activo → devolve apenas as formas configuradas pelo lojista.
 * - Com pagamento online activo:
 *   - Injecta a opção que dispara o Mercado Pago no topo (rótulo customizado
 *     da loja, se houver, senão o padrão).
 *   - Se o lojista não configurou nenhuma forma offline, adiciona também
 *     "PAGAR NA ENTREGA / RETIRADA" para o cliente ter onde escolher.
 *
 * @param {Array<string>|null|undefined} tenantList Lista guardada na empresa (já normalizada).
 * @param {boolean} paymentOnlineEnabled
 * @param {string|null|undefined} customOnlineLabel  Texto customizado pela loja.
 * @returns {CheckoutPaymentOption[]}
 */
export function buildEffectiveCheckoutPaymentMethods(
  tenantList,
  paymentOnlineEnabled,
  customOnlineLabel = null,
) {
  const cleanTenant = Array.isArray(tenantList)
    ? tenantList
        .map((x) => String(x ?? '').trim())
        .filter(Boolean)
    : [];

  if (!paymentOnlineEnabled) {
    return cleanTenant.map((value) => ({ value, display: value, kind: 'tenant' }));
  }

  const customClean = String(customOnlineLabel ?? '').trim();
  const onlineDisplay = customClean !== '' ? customClean : CHECKOUT_PM_ONLINE_DISPLAY;
  const onlineValueRaw = normalizeCheckoutPaymentLabel(onlineDisplay);
  const onlineValue = onlineValueRaw || CHECKOUT_PM_ONLINE_VALUE;

  const out = [
    {
      value: onlineValue,
      display: onlineDisplay,
      kind: 'online',
      subtitle: 'Cartão · Pix · Boleto via Mercado Pago',
    },
  ];

  for (const value of cleanTenant) {
    if (value === onlineValue) continue;
    out.push({ value, display: value, kind: 'tenant' });
  }

  const hasOfflineOption = out.some((opt) => opt.kind !== 'online');
  if (!hasOfflineOption) {
    out.push({
      value: CHECKOUT_PM_OFFLINE_FALLBACK_VALUE,
      display: CHECKOUT_PM_OFFLINE_FALLBACK_DISPLAY,
      kind: 'offline-fallback',
    });
  }

  return out;
}

/** Devolve a opção que corresponde a um `value`, ou null se não existir. */
export function findCheckoutPaymentOption(options, value) {
  if (!Array.isArray(options) || !value) return null;
  return options.find((opt) => opt.value === value) || null;
}

/** Indica se o valor recebido corresponde à opção que dispara o Mercado Pago. */
export function checkoutPaymentIsOnline(value, options) {
  if (Array.isArray(options) && options.length > 0) {
    const opt = findCheckoutPaymentOption(options, value);
    return Boolean(opt && opt.kind === 'online');
  }
  return typeof value === 'string' && value.trim().toUpperCase() === CHECKOUT_PM_ONLINE_VALUE;
}
